class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.sectionLoadHandler = () => this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.setHeaderCartIconAccessibility();
    document.addEventListener('shopify:section:load', this.sectionLoadHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this.sectionLoadHandler);
    this.unbindHeaderCartIcon();
    if (this.openTimeout != null || this.classList.contains('active')) this.close();
  }

  unbindHeaderCartIcon() {
    if (!this.boundCartLink) return;
    this.boundCartLink.removeEventListener('click', this.cartClickHandler);
    this.boundCartLink.removeEventListener('keydown', this.cartKeyHandler);
    this.boundCartLink = null;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink === this.boundCartLink) return;
    this.unbindHeaderCartIcon();
    if (!cartLink) return;
    this.boundCartLink = cartLink;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    this.cartClickHandler = (event) => {
      event.preventDefault();
      this.open(cartLink);
    };
    this.cartKeyHandler = (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    };
    cartLink.addEventListener('click', this.cartClickHandler);
    cartLink.addEventListener('keydown', this.cartKeyHandler);
  }

  open(triggeredBy) {
    if (this.classList.contains('active') || this.openTimeout != null) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // Trap focus as soon as the drawer opens, including when motion is reduced.
    this.openTimeout = setTimeout(() => {
      this.openTimeout = null;
      this.classList.add('animate', 'active');
      const focusDrawer = () => {
        if (!this.classList.contains('active')) return;
        const container = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        trapFocus(container, container.querySelector('.drawer__close') || container);
      };
      this.focusFrame = requestAnimationFrame(() => {
        this.focusFrame = null;
        focusDrawer();
      });
      // Some browsers defer visibility until the drawer transition has started.
      // Retry only when focus is still outside, and never after a close.
      const durations = getComputedStyle(this.querySelector('.drawer__inner')).transitionDuration.split(',');
      const durationMs = Math.max(...durations.map(value => parseFloat(value) * (value.trim().endsWith('ms') ? 1 : 1000)));
      this.focusTimeout = setTimeout(() => {
        this.focusTimeout = null;
        if (!this.contains(document.activeElement)) focusDrawer();
      }, (Number.isFinite(durationMs) ? durationMs : 200) + 50);
    });

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    clearTimeout(this.openTimeout);
    this.openTimeout = null;
    cancelAnimationFrame(this.focusFrame);
    this.focusFrame = null;
    clearTimeout(this.focusTimeout);
    this.focusTimeout = null;
    this.classList.remove('active');
    removeTrapFocus(this.getReturnFocusElement());
    document.body.classList.remove('overflow-hidden');
  }

  getReturnFocusElement() {
    const isVisible = (element) => {
      if (!element?.isConnected || element.disabled || element.closest('[hidden], [inert]')) return false;
      if (!element.getClientRects().length) return false;
      const style = getComputedStyle(element);
      return style.display !== 'none' && style.visibility === 'visible';
    };
    // The opener may disappear after a resize, header refresh or quick-add close.
    const candidates = [this.activeElement, document.getElementById('cart-icon-bubble')];
    return candidates.find(isVisible) || Array.from(document.querySelectorAll('a[href="/cart"]')).find(isVisible);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
